import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CalendarPlus, CheckCircle2, ChevronDown, FileSignature, Loader2,
  Plus, Sparkles, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDate } from "@/lib/contracts";
import { cn } from "@/lib/utils";
import { useLang, translateFr as tfr } from "@/i18n";
import api from "@/lib/api";
import { toast } from "sonner";

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  return (typeof d === "object" ? d?.message : d) || "Erreur";
};

const STATE_CLS = {
  OVERDUE: "bg-red-500/10 text-red-400 border-red-500/25",
  DUE_TODAY: "bg-red-500/10 text-red-400 border-red-500/25",
  DUE_SOON: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/25",
  DECISION_REQUIRED: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/25",
  UPCOMING: "bg-blue-500/10 text-blue-400 border-blue-500/25",
  AVAILABLE: "bg-blue-500/10 text-blue-400 border-blue-500/25",
  PENDING: "bg-zinc-500/10 text-zinc-400 border-zinc-500/25",
  IN_PROGRESS: "bg-blue-500/10 text-blue-400 border-blue-500/25",
  COMPLETED: "bg-green-500/10 text-green-400 border-green-500/25",
  EXERCISED: "bg-green-500/10 text-green-400 border-green-500/25",
  NOT_EXERCISED: "bg-zinc-500/10 text-zinc-500 border-zinc-500/20",
  WAIVED: "bg-zinc-500/10 text-zinc-500 border-zinc-500/20",
  CANCELLED: "bg-zinc-500/10 text-zinc-500 border-zinc-500/20",
  EXPIRED: "bg-orange-500/10 text-orange-400 border-orange-500/25",
  DISPUTED: "bg-red-500/10 text-red-400 border-red-500/25",
};
const STATE_FR = {
  OVERDUE: tfr("En retard"), DUE_TODAY: tfr("Aujourd'hui"), DUE_SOON: tfr("Bientôt"), UPCOMING: tfr("À venir"),
  DECISION_REQUIRED: tfr("Décision requise"), AVAILABLE: "Disponible", PENDING: tfr("En attente"),
  IN_PROGRESS: tfr("En cours"), COMPLETED: tfr("Complétée"), EXERCISED: tfr("Exercée"),
  NOT_EXERCISED: tfr("Non exercée"), WAIVED: tfr("Levée"), CANCELLED: tfr("Annulée"), EXPIRED: tfr("Expirée"),
  DISPUTED: tfr("Contestée"),
};
const HEALTH = {
  ON_TRACK: { label: tfr("Sous contrôle"), cls: "bg-green-500/10 text-green-400 border-green-500/25" },
  ATTENTION: { label: "Attention requise", cls: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/25" },
  CRITICAL: { label: "Critique", cls: "bg-red-500/10 text-red-400 border-red-500/25" },
};
const DL_CATS = [["option_exercise", tfr("Option à exercer")], ["bonus", "Prime"], ["payment", "Paiement"],
  ["registration", "Enregistrement"], ["document", "Document"], ["insurance", "Assurance"],
  ["image_rights", tfr("Droits à l'image")], ["review", tfr("Revue")], ["notice", tfr("Préavis")],
  ["termination", tfr("Résiliation")], ["extension", "Prolongation"], ["release_clause", tfr("Clause libératoire")],
  ["renewal", "Renouvellement"], ["custom", "Autre"]];
const PARTIES = [["club", "Club"], ["athlete", tfr("Athlète")], ["agent", "Agent"], ["other", "Autre"]];

const _sigEn = (s) => s
  .replace(/^(\d+) obligation\(s\) en retard$/, "$1 overdue obligation(s)")
  .replace(/^(\d+) échéance\(s\) dépassée\(s\)$/, "$1 missed deadline(s)")
  .replace(/^Échéance « (.+) » dans (\d+) j$/, 'Deadline "$1" in $2 d')
  .replace(/^Option « (.+) » à décider dans (\d+) j$/, 'Option "$1" to decide in $2 d')
  .replace(/^Préparation du renouvellement en cours$/, "Renewal preparation in progress")
  .replace(/^Préparation du renouvellement dans (\d+) j$/, "Renewal preparation in $1 d");
const translateSignal = (s) => {
  try { return localStorage.getItem("athlynx_lang") === "en" ? _sigEn(s) : s; } catch { return s; }
};

const StateChip = ({ state, tf }) => (
  <span className={cn("inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium shrink-0",
    STATE_CLS[state] || STATE_CLS.PENDING)}>{tf(STATE_FR[state] || state)}</span>
);

const Source = ({ item, tf }) => (item.source_clause || item.source_page) ? (
  <p className="text-[10px] text-muted-foreground mt-0.5">
    {tf("Source :")} {[item.source_clause, item.source_page ? `${tf("Page")} ${item.source_page}` : null].filter(Boolean).join(" · ")}
    {item.confidence === "review_required" && <span className="text-amber-600 dark:text-amber-400"> · {tf("À vérifier")}</span>}
  </p>
) : null;

const Section = ({ title, count, open, onToggle, action, children, testid }) => (
  <div className="rounded-xl border border-border" data-testid={testid}>
    <button onClick={onToggle} className="w-full flex items-center justify-between px-4 py-3" data-testid={`${testid}-toggle`}>
      <span className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {title} {count > 0 && <span className="ml-1.5 text-foreground">{count}</span>}
      </span>
      <span className="flex items-center gap-2">{action}<ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", open && "rotate-180")} /></span>
    </button>
    {open && <div className="px-4 pb-4">{children}</div>}
  </div>
);

export const OpsPanel = ({ contract, onChanged }) => {
  const { tf, lang } = useLang();
  const [ops, setOps] = useState(null);
  const [open, setOpen] = useState({ deadlines: true, obligations: false, options: false, amendments: false });
  const [dlg, setDlg] = useState(null); // {kind}
  const [form, setForm] = useState({});
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [proposals, setProposals] = useState(null);
  const [checked, setChecked] = useState({});

  const load = useCallback(async () => {
    try { setOps((await api.get(`/contracts/${contract.id}/ops`)).data); } catch { /* silent */ }
  }, [contract.id]);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); onChanged?.(); };

  if (!ops) return null;
  const { health, summary } = ops;
  const h = HEALTH[health.level] || HEALTH.ON_TRACK;
  const fmtRemaining = (days) => {
    if (days == null || days < 0) return "—";
    const y = Math.floor(days / 365), m = Math.floor((days % 365) / 30);
    if (y > 0) return `${y} ${tf("an(s)")} · ${m} ${tf("mois")}`;
    return m > 0 ? `${m} ${tf("mois")}` : `${days} j`;
  };

  const act = async (fn, ok) => { try { await fn(); ok && toast.success(tf(ok)); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const submit = async () => {
    const k = dlg.kind;
    const urls = { deadline: `/contracts/${contract.id}/deadlines`, obligation: `/contracts/${contract.id}/obligations`,
                   option: `/contracts/${contract.id}/options`, amendment: `/contracts/${contract.id}/amendments` };
    const payload = k === "amendment"
      ? { title: form.title, effective_date: form.due_at || null,
          changes: [form.field && form.new_value ? { field: form.field, new_value: form.new_value } : null].filter(Boolean) }
      : k === "option"
        ? { label: form.title, deadline: form.due_at, holder: form.party || "club", conditions: form.notes || "", source_clause: form.source_clause }
        : { title: form.title, due_at: form.due_at || null, category: form.category,
            party: form.party || "club", source_clause: form.source_clause, notes: form.notes };
    await act(async () => { await api.post(urls[k], payload); setDlg(null); setForm({}); }, tfr("Créé"));
  };

  const analyze = async () => {
    setAiBusy(true);
    try {
      const r = await api.post(`/contracts/${contract.id}/ops/extract`, {});
      setProposals(r.data);
      const all = {};
      ["deadlines", "obligations", "options"].forEach((k) =>
        r.data.proposals[k].forEach((p, i) => { all[`${k}:${i}`] = p.confidence === "confirmed"; }));
      setChecked(all);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setAiBusy(false); }
  };
  const validateProposals = async () => {
    const items = [];
    ["deadlines", "obligations", "options"].forEach((k) =>
      (proposals?.proposals[k] || []).forEach((p, i) => { if (checked[`${k}:${i}`]) items.push(p); }));
    if (!items.length) return toast.error(tf("Sélectionnez au moins un élément"));
    await act(async () => {
      const r = await api.post(`/contracts/${contract.id}/ops/bulk-create`, { items });
      toast.success(`${Object.values(r.data.created).reduce((a, b) => a + b, 0)} ${tf("élément(s) créé(s)")}`);
      setAiOpen(false); setProposals(null);
    });
  };

  return (
    <section className="rounded-2xl border border-border bg-surface p-5 space-y-4" data-testid="ops-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">{tf("Suivi opérationnel")}</h2>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className={cn("inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold", h.cls)} data-testid="ops-health-badge">{tf(h.label)}</span>
            {summary.end_days_left != null && summary.end_days_left >= 0 && (
              <span className="text-[11px] text-muted-foreground" data-testid="ops-time-remaining">{tf("Temps restant :")} {fmtRemaining(summary.end_days_left)}</span>
            )}
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={() => { setAiOpen(true); if (!proposals) analyze(); }} data-testid="ops-ai-analyze-btn">
          <Sparkles className="h-3.5 w-3.5 mr-1.5" /> {tf("Analyser le contrat")}
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
        {[["ops-kpi-next", tf("Prochaine action"), summary.next_action ? `${summary.next_action.title}`.slice(0, 26) : "—",
           summary.next_action ? fmtDate(summary.next_action.due_at) : ""],
          ["ops-kpi-deadlines", tf("Échéances à venir"), summary.upcoming_deadlines, ""],
          ["ops-kpi-obligations", tf("Obligations ouvertes"), summary.open_obligations, ""],
          ["ops-kpi-renewal", tf("Renouvellement"), summary.renewal_prep_days == null ? "—"
            : summary.renewal_prep_days === 0 ? tf("Préparation en cours") : `${tf("Préparation dans")} ${summary.renewal_prep_days} ${tf("j")}`, ""],
        ].map(([tid, label, value, sub]) => {
          const renewalAttention = tid === "ops-kpi-renewal" && summary.renewal_prep_days != null && summary.renewal_prep_days <= 90;
          return (
          <div key={tid}
            className={cn("rounded-xl border px-2 py-2.5",
              renewalAttention ? "border-amber-500/30 bg-amber-500/10" : "border-border bg-secondary/30")}
            data-testid={tid}>
            <p className={cn("text-[10px] uppercase tracking-wide",
              renewalAttention ? "text-amber-700 dark:text-amber-300" : "text-muted-foreground")}>{label}</p>
            <p className="text-sm font-semibold mt-0.5 truncate">{value}</p>
            {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
          </div>
          );
        })}
      </div>
      {health.signals.length > 0 && (
        <ul className="space-y-1.5 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2.5" data-testid="ops-health-signals">
          {health.signals.slice(0, 4).map((s, i) => (
            <li key={i} className="text-xs text-amber-800 dark:text-amber-200 flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0" /> {translateSignal(s)}
            </li>
          ))}
        </ul>
      )}

      <div className="space-y-2">
        <Section title={tf("Échéances")} count={ops.deadlines.filter((d) => d.status === "pending").length} testid="ops-deadlines"
          open={open.deadlines} onToggle={() => setOpen((o) => ({ ...o, deadlines: !o.deadlines }))}
          action={<Plus className="h-4 w-4 text-muted-foreground" onClick={(e) => { e.stopPropagation(); setForm({}); setDlg({ kind: "deadline" }); }} data-testid="add-deadline-btn" />}>
          {ops.deadlines.length === 0 ? <p className="text-xs text-muted-foreground" data-testid="deadlines-empty">{tf("Aucune échéance contractuelle à venir.")}</p> : (
            <ul className="space-y-1.5">
              {ops.deadlines.map((d) => (
                <li key={d.id} className="flex items-start gap-2.5 rounded-lg border border-border px-3 py-2" data-testid={`deadline-${d.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium break-words">{d.title}</p>
                    <p className="text-[10px] text-muted-foreground">{fmtDate(d.due_at)}{d.days_left != null && d.status === "pending" ? ` · ${d.days_left} j` : ""}</p>
                    <Source item={d} tf={tf} />
                  </div>
                  <StateChip state={d.state} tf={tf} />
                  {d.status === "pending" && (
                    <div className="flex gap-0.5 shrink-0">
                      <Button size="icon" variant="ghost" className="h-6 w-6" title={tf("Ajouter au calendrier")}
                        onClick={() => act(() => api.post(`/contract-deadlines/${d.id}/calendar`), tfr("Ajouté au calendrier"))} data-testid={`deadline-calendar-${d.id}`}>
                        <CalendarPlus className="h-3 w-3" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-green-400" title={tf("Marquer complétée")}
                        onClick={() => act(() => api.patch(`/contract-deadlines/${d.id}`, { status: "completed" }), tfr("Échéance complétée"))} data-testid={`deadline-complete-${d.id}`}>
                        <CheckCircle2 className="h-3 w-3" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-red-400"
                        onClick={() => act(() => api.delete(`/contract-deadlines/${d.id}`))} data-testid={`deadline-delete-${d.id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title={tf("Obligations")} count={ops.obligations.filter((o) => ["pending", "in_progress", "overdue"].includes(o.status)).length}
          testid="ops-obligations" open={open.obligations} onToggle={() => setOpen((o) => ({ ...o, obligations: !o.obligations }))}
          action={<Plus className="h-4 w-4 text-muted-foreground" onClick={(e) => { e.stopPropagation(); setForm({}); setDlg({ kind: "obligation" }); }} data-testid="add-obligation-btn" />}>
          {ops.obligations.length === 0 ? <p className="text-xs text-muted-foreground" data-testid="obligations-empty">{tf("Aucune obligation suivie pour ce contrat.")}</p> : (
            <ul className="space-y-1.5">
              {ops.obligations.map((o) => (
                <li key={o.id} className="flex items-start gap-2.5 rounded-lg border border-border px-3 py-2" data-testid={`obligation-${o.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium break-words">{o.title}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {tf(PARTIES.find((p) => p[0] === o.party)?.[1] || o.party)}{o.due_at ? ` · ${fmtDate(o.due_at)}` : ""}
                    </p>
                    <Source item={o} tf={tf} />
                  </div>
                  <StateChip state={o.state} tf={tf} />
                  {!["completed", "waived", "cancelled"].includes(o.status) && (
                    <Button size="icon" variant="ghost" className="h-6 w-6 text-green-400 shrink-0" title={tf("Marquer complétée")}
                      onClick={() => act(() => api.patch(`/contract-obligations/${o.id}`, { status: "completed" }), tfr("Obligation complétée"))} data-testid={`obligation-complete-${o.id}`}>
                      <CheckCircle2 className="h-3 w-3" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title={tf("Options")} count={ops.options.filter((o) => ["available", "decision_required"].includes(o.status)).length}
          testid="ops-options" open={open.options} onToggle={() => setOpen((o) => ({ ...o, options: !o.options }))}
          action={<Plus className="h-4 w-4 text-muted-foreground" onClick={(e) => { e.stopPropagation(); setForm({}); setDlg({ kind: "option" }); }} data-testid="add-option-btn" />}>
          {ops.options.length === 0 ? <p className="text-xs text-muted-foreground" data-testid="options-empty">{tf("Aucune option contractuelle suivie.")}</p> : (
            <ul className="space-y-1.5">
              {ops.options.map((o) => (
                <li key={o.id} className="rounded-lg border border-border px-3 py-2" data-testid={`option-${o.id}`}>
                  <div className="flex items-start gap-2.5">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium break-words">{o.label}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {tf("Échéance")} {fmtDate(o.deadline)}{o.days_left != null ? ` · ${o.days_left} j` : ""} · {tf("Détenteur :")} {tf(PARTIES.find((p) => p[0] === o.holder)?.[1] || o.holder)}
                      </p>
                      {o.conditions && <p className="text-[10px] text-muted-foreground mt-0.5">{o.conditions}</p>}
                      <Source item={o} tf={tf} />
                    </div>
                    <StateChip state={o.state} tf={tf} />
                  </div>
                  {["available", "decision_required"].includes(o.status) && (
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" variant="outline" className="h-6 text-[10px]" data-testid={`option-exercise-${o.id}`}
                        onClick={() => act(() => api.post(`/contract-options/${o.id}/decision`, { decision: "exercised" }), tfr("Option exercée"))}>
                        {tf("Exercer")}
                      </Button>
                      <Button size="sm" variant="ghost" className="h-6 text-[10px] text-muted-foreground" data-testid={`option-decline-${o.id}`}
                        onClick={() => act(() => api.post(`/contract-options/${o.id}/decision`, { decision: "not_exercised" }), tfr("Option non exercée"))}>
                        {tf("Ne pas exercer")}
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title={tf("Avenants")} count={ops.amendments.length} testid="ops-amendments"
          open={open.amendments} onToggle={() => setOpen((o) => ({ ...o, amendments: !o.amendments }))}
          action={<Plus className="h-4 w-4 text-muted-foreground" onClick={(e) => { e.stopPropagation(); setForm({}); setDlg({ kind: "amendment" }); }} data-testid="add-amendment-btn" />}>
          {ops.amendments.length === 0 ? <p className="text-xs text-muted-foreground" data-testid="amendments-empty">{tf("Aucun avenant enregistré.")}</p> : (
            <ul className="space-y-1.5">
              {ops.amendments.map((a) => (
                <li key={a.id} className="rounded-lg border border-border px-3 py-2" data-testid={`amendment-${a.id}`}>
                  <div className="flex items-start gap-2.5">
                    <FileSignature className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium break-words">{a.title}</p>
                      {(a.changes || []).map((ch, i) => (
                        <p key={i} className="text-[10px] text-muted-foreground">
                          {ch.field} : <span className="line-through">{String(ch.original_value ?? "—")}</span> → <span className="text-foreground">{ch.new_value}</span>
                        </p>
                      ))}
                    </div>
                    <span className={cn("inline-flex rounded-full border px-2 py-0.5 text-[10px] shrink-0",
                      a.status === "validated" ? STATE_CLS.COMPLETED : STATE_CLS.PENDING)}>
                      {a.status === "validated" ? tf("Validé") : tf("Brouillon")}
                    </span>
                  </div>
                  {a.status === "draft" && (
                    <Button size="sm" variant="outline" className="h-6 text-[10px] mt-2" data-testid={`amendment-validate-${a.id}`}
                      onClick={() => act(() => api.post(`/contract-amendments/${a.id}/validate`), tfr("Avenant validé — termes opérationnels mis à jour"))}>
                      {tf("Valider l'avenant")}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      {/* add dialog */}
      <Dialog open={!!dlg} onOpenChange={(v) => !v && setDlg(null)}>
        <DialogContent className="sm:max-w-[460px]" data-testid="ops-add-dialog">
          <DialogHeader>
            <DialogTitle>
              {tf({ deadline: tfr("Nouvelle échéance"), obligation: tfr("Nouvelle obligation"), option: tfr("Nouvelle option"), amendment: tfr("Nouvel avenant") }[dlg?.kind] || "")}
            </DialogTitle>
            <DialogDescription>{tf("Donnée contractuelle validée par vous.")}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-1">
            <div className="grid gap-1.5">
              <Label>{tf("Titre")} *</Label>
              <Input data-testid="ops-form-title" value={form.title || ""} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
            </div>
            {dlg?.kind === "deadline" && (
              <div className="grid gap-1.5">
                <Label>{tf("Catégorie")}</Label>
                <Select value={form.category || "custom"} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger data-testid="ops-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{DL_CATS.map(([id, l]) => <SelectItem key={id} value={id}>{tf(l)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {(dlg?.kind === "obligation" || dlg?.kind === "option") && (
              <div className="grid gap-1.5">
                <Label>{dlg.kind === "option" ? tf("Détenteur") : tf("Partie obligée")}</Label>
                <Select value={form.party || "club"} onValueChange={(v) => setForm((f) => ({ ...f, party: v }))}>
                  <SelectTrigger data-testid="ops-form-party"><SelectValue /></SelectTrigger>
                  <SelectContent>{PARTIES.map(([id, l]) => <SelectItem key={id} value={id}>{tf(l)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {dlg?.kind === "amendment" && (
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5">
                  <Label>{tf("Terme modifié")}</Label>
                  <Select value={form.field || ""} onValueChange={(v) => setForm((f) => ({ ...f, field: v }))}>
                    <SelectTrigger data-testid="ops-form-field"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {[["value_amount", "Montant"], ["end_date", "Date de fin"], ["start_date", tfr("Date de début")],
                        ["commission_percent", "Commission %"], ["counterparty", "Contrepartie"]].map(([id, l]) =>
                        <SelectItem key={id} value={id}>{tf(l)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-1.5">
                  <Label>{tf("Nouvelle valeur")}</Label>
                  <Input data-testid="ops-form-new-value" value={form.new_value || ""} onChange={(e) => setForm((f) => ({ ...f, new_value: e.target.value }))} />
                </div>
              </div>
            )}
            <div className="grid gap-1.5">
              <Label>{dlg?.kind === "amendment" ? tf("Date d'effet") : tf("Échéance")}{dlg?.kind === "deadline" || dlg?.kind === "option" ? " *" : ""}</Label>
              <Input type="date" data-testid="ops-form-date" value={form.due_at || ""} onChange={(e) => setForm((f) => ({ ...f, due_at: e.target.value }))} />
            </div>
            <div className="grid gap-1.5">
              <Label>{tf("Clause source (optionnel)")}</Label>
              <Input data-testid="ops-form-clause" placeholder="Article 4.2" value={form.source_clause || ""} onChange={(e) => setForm((f) => ({ ...f, source_clause: e.target.value }))} />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setDlg(null)}>{tf("Annuler")}</Button>
            <Button onClick={submit} data-testid="ops-form-save">{tf("Créer")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI proposals dialog */}
      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="sm:max-w-[560px] max-h-[80vh] overflow-y-auto" data-testid="ops-ai-dialog">
          <DialogHeader>
            <DialogTitle>{tf("Analyse Athlynx Intelligence")}</DialogTitle>
            <DialogDescription>{tf("Suggestions extraites du document — rien n'est créé sans votre validation.")}</DialogDescription>
          </DialogHeader>
          {aiBusy ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : proposals ? (
            <div className="space-y-4">
              {[["deadlines", tf("Échéances")], ["obligations", tf("Obligations")], ["options", tf("Options")]].map(([k, label]) => (
                <div key={k}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{label} ({proposals.proposals[k].length})</p>
                  {proposals.proposals[k].length === 0 ? <p className="text-[11px] text-muted-foreground">—</p> : (
                    <ul className="space-y-1.5">
                      {proposals.proposals[k].map((p, i) => (
                        <li key={i} className="flex items-start gap-2.5 rounded-lg border border-border px-3 py-2" data-testid={`ai-proposal-${k}-${i}`}>
                          <Checkbox checked={!!checked[`${k}:${i}`]} onCheckedChange={(v) => setChecked((c) => ({ ...c, [`${k}:${i}`]: !!v }))} className="mt-0.5" />
                          <div className="min-w-0 flex-1">
                            <p className="text-xs">{p.title || p.label}</p>
                            <p className="text-[10px] text-muted-foreground">
                              {[p.due_at || p.deadline, p.source_clause, p.source_page ? `${tf("Page")} ${p.source_page}` : null].filter(Boolean).join(" · ")}
                            </p>
                          </div>
                          <span className={cn("text-[9px] rounded-full border px-1.5 py-0.5 shrink-0",
                            p.confidence === "confirmed" ? STATE_CLS.COMPLETED : STATE_CLS.DUE_SOON)}>
                            {p.confidence === "confirmed" ? tf("Confirmé") : tf("À vérifier")}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setAiOpen(false)}>{tf("Annuler")}</Button>
                <Button onClick={validateProposals} data-testid="ai-validate-btn">{tf("Valider la sélection")}</Button>
              </div>
            </div>
          ) : <p className="text-xs text-muted-foreground py-6 text-center">{tf("Analyse indisponible.")}</p>}
        </DialogContent>
      </Dialog>
    </section>
  );
};
